import { useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { FleetView } from './FleetView.js';
import { useFleetQuery } from './useFleetQuery.js';

/**
 * The `/fleet` route component (promoted from a ComingSoon stub this plan). Reads the typed search
 * params (`entryType`, `maintenanceCategory`, `archived`), fires the coarse-endpoint TanStack
 * Query, and renders the faithful `FleetView` card grid on success. Loading → accessible
 * a page-shaped `RouteSkeleton`; error → announced error state with a retry that refetches (the reports `ReportsRoute`
 * 3-state contract).
 *
 * The server computes the vehicle list + entry rollups + the MTD summary; this component never
 * re-aggregates and never sends a tenant id (SEC-11).
 */

const routeApi = getRouteApi('/fleet');

export function FleetRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = useFleetQuery(search);

  if (query.isPending) {
    return <RouteSkeleton label={t('fleet:title')} rows={6} columns={4} filters={3} />;
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('fleet:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('fleet:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('fleet:error.retry')}
        </button>
      </section>
    );
  }

  return <FleetView data={query.data} search={search} />;
}
